import { useCallback, useMemo, useState } from 'react';
import moment from 'moment';
import { capitalize } from 'lodash-es';
import { ITableActionConfig } from '../../TableComponents/types';
import { downloadFile } from '@home/utils/file';
import { copyToClipboard } from '@/pages/aigc/utils/utils';
import copyIcon from '@/pages/aigc/assets/svg/agent-table-copy.svg';
import copyActiveIcon from '@/pages/aigc/assets/svg/agent-table-copy-active.svg';
import downloadIcon from '@/pages/aigc/assets/svg/agent-table-download.svg';
import downloadActiveIcon from '@/pages/aigc/assets/svg/agent-table-download-active.svg';
import collapseIcon from '@/pages/aigc/assets/svg/agent-code-collapse.svg';
import collapseActiveIcon from '@/pages/aigc/assets/svg/agent-code-collapse-active.svg';
import expandIcon from '@/pages/aigc/assets/svg/agent-code-expand.svg';
import expandActiveIcon from '@/pages/aigc/assets/svg/agent-code-expand-active.svg';
import { EXTENSION_MAP, LANGUAGE_MODE_MAP } from '../../../../config/constant';

/**
 * useCodeActions Hook 参数接口
 */
export interface IUseCodeActionsParams {
  /** 代码内容（可以是字符串或任意类型数组） */
  children?: any[];

  /**
   * 类名（包含语言信息）
   * @description Markdown 会自动为代码块添加 'language-xxx' 格式的类名
   */
  className?: string;
}

/**
 * useCodeActions Hook
 * @param params - Hook 参数对象
 * @returns 返回包含代码操作配置和相关方法的对象
 */
export const useCodeActions = ({
  children,
  className,
}: IUseCodeActionsParams) => {
  /**
   * 从 className 中提取语言类型（小写，用于内部逻辑）
   * @description 通过正则匹配 'language-xxx' 格式提取语言标识
   */
  const languageKey = useMemo(() => {
    const match = /language-(\w+)/.exec(className || '');
    return match ? match[1] : '';
  }, [className]);

  /**
   * 显示用的语言名称（首字母大写）
   */
  const language = useMemo(() => capitalize(languageKey), [languageKey]);
  /**
   * 获取 Ace Editor 的 mode
   * @description 根据语言类型映射到对应的 Ace Editor mode
   */
  const aceMode = useMemo(() => LANGUAGE_MODE_MAP[languageKey] || 'text', [languageKey]);

  /**
   * 提取并处理代码值
   * @description 将 children 转换为字符串并去除末尾换行符
   */
  const codeValue = useMemo(() => String(children).replace(/\n$/, ''), [children]);

  /**
   * 代码行数
   * @description 基于换行符切分；空内容按 0 行处理
   */
  const lineCount = useMemo(() => {
    if (!codeValue) return 0;
    return codeValue.split('\n').length;
  }, [codeValue]);

  /**
   * 折叠状态（内敛在 hook 内部，避免父组件维护多份 UI 状态）
   */
  const [collapsed, setCollapsed] = useState<boolean>(false);

  /**
   * 切换折叠态
   * @returns 始终返回 false，告知 TableActions 不要进入"成功高亮 2s"的副作用
   */
  const handleToggleCollapse = useCallback((): boolean => {
    setCollapsed(prev => !prev);
    return false;
  }, []);

  /**
   * 复制代码到剪贴板
   * @returns 返回操作是否成功
   */
  const handleCopyCode = useCallback(async (): Promise<boolean> => {
    const success = await copyToClipboard(codeValue);
    return success;
  }, [codeValue]);

  /**
   * 下载代码为文件
   * @description 根据语言类型自动选择文件扩展名，生成带时间戳的文件名
   * @returns 返回操作是否成功
   */
  const handleDownloadCode = useCallback(async (): Promise<boolean> => {
    const fileExtension = EXTENSION_MAP[languageKey] || '.txt';

    // 使用 moment 生成格式化的时间字符串：file_年-月-日_时-分.扩展名
    const dateStr = moment().format('YYYY-MM-DD_HH_mm');
    const fileName = `file_${dateStr}${fileExtension}`;

    const blob = new Blob([codeValue], { type: 'text/plain' });
    downloadFile(blob, fileName);
    return true;
  }, [codeValue, languageKey]);
  /**
   * Ace Editor 加载完成回调
   * @description 只读展示：禁插入、藏光标。textarea 用 readOnly 而非 disabled，
   * 也不 off change/changeSelection，否则无法拖选和 Ctrl/Cmd+C。
   * 卸掉 Ace `$readOnlyCallback`，避免按键弹出 Editing is disabled。
   * @param editor - Ace Editor 实例
   */
  const handleEditorLoad = (editor: any) => {
    // 禁用插入类命令，避免只读态仍能改内容
    editor.commands.removeCommands(['insertstring', 'backspace', 'del', 'enter']);

    // 隐藏光标，保持只读观感（不影响选区高亮）
    editor.renderer.$cursorLayer.element.style.display = 'none';

    // Ace 用隐藏 textarea 承接选区和剪贴板；disabled 会禁掉复制，readOnly 仍可选中复制
    const textInput = editor.textInput.getElement();
    textInput.readOnly = true;

    // Ace 只读会在 keydown / commandUnavailable 弹出 Editing is disabled；只卸这条回调
    if (editor.$readOnlyCallback) {
      textInput.removeEventListener('keydown', editor.$readOnlyCallback);
      editor.commands.off('exec', editor.$readOnlyCallback);
      editor.commands.off('commandUnavailable', editor.$readOnlyCallback);
    }
  };

  /**
   * 代码操作配置数组
   * @description 顺序：复制 -> 下载 -> 折叠/展开（折叠按钮排在最后）
   */
  const codeActions: ITableActionConfig[] = useMemo(() => [
    {
      type: 'copy',
      icon: copyIcon,
      activeIcon: copyActiveIcon,
      tooltipKey: 'dba_code_copy',
      successTooltipKey: 'dba_common_copy_success',
      autoHideTooltip: true, // 复制成功后2秒自动隐藏提示
      onClick: handleCopyCode,
    },
    {
      type: 'download',
      icon: downloadIcon,
      activeIcon: downloadActiveIcon,
      tooltipKey: 'dba_code_download',
      autoHideTooltip: false, // 下载操作不自动隐藏提示
      onClick: handleDownloadCode,
    },
    {
      type: 'collapse',
      // 已折叠：显示向下箭头(collapseIcon)，提示「展开」；已展开：显示向上箭头(expandIcon)，提示「收起」
      icon: collapsed ? collapseIcon : expandIcon,
      activeIcon: collapsed ? collapseActiveIcon : expandActiveIcon,
      tooltipKey: collapsed ? 'dba_code_expand' : 'dba_code_collapse',
      autoHideTooltip: false,
      // UI 切换型操作：绕过 TableActions 默认的 500ms 防抖，避免连续点击有停顿感
      instant: true,
      onClick: handleToggleCollapse,
    },
  ], [handleCopyCode, handleDownloadCode, handleToggleCollapse, collapsed]);

  return {
    codeActions,
    codeValue,
    language,
    aceMode,
    collapsed,
    lineCount,
    handleCopyCode,
    handleDownloadCode,
    handleToggleCollapse,
    handleEditorLoad,
  };
};

export default useCodeActions;
